import { TimelineConfiguration } from "@/data/models/TimelineConfiguration";
import { PlanningBoardSettings } from "@/data/models/SchedulingSettings";
import { DateTime, Duration, Interval } from "luxon";

export enum TimelineVisibleArea {
  ONE_DAY,
  ONE_WEEK,
  TWO_WEEKS,
  FOUR_WEEKS,
}

export function localiseTimelineVisibleArea(
  vue: Vue,
  timelineVisibleArea: TimelineVisibleArea
) {
  switch (timelineVisibleArea) {
    case TimelineVisibleArea.ONE_DAY:
      return vue.$t("scheduling.timelineSettings.visibleArea.oneDay");
    case TimelineVisibleArea.ONE_WEEK:
      return vue.$t("scheduling.timelineSettings.visibleArea.oneWeek");
    case TimelineVisibleArea.TWO_WEEKS:
      return vue.$t("scheduling.timelineSettings.visibleArea.twoWeeks");
    case TimelineVisibleArea.FOUR_WEEKS:
      return vue.$t("scheduling.timelineSettings.visibleArea.fourWeeks");
  }
}

export interface HiddenDateData {
  start: string;
  end: string;
  repeatDaily?: boolean;
  repeatWeekly?: boolean;
}

export interface TimelineSettings {
  startDate: Date;
  visibleArea: TimelineVisibleArea;
  showWeekends: boolean;
  onlyShowWorkingHours: boolean;
}

export interface WorkDaySettings {
  workDayBufferInMinutes: number;
  workDayStart: {
    hour: number;
    minutes: number;
  };
  workDayEnd: {
    hour: number;
    minutes: number;
  };
}

function addHighlightHoursForDay(
  highlightDates: Date[],
  dateTime: DateTime,
  highlightHours: number[]
) {
  highlightHours.forEach((hour) =>
    highlightDates.push(
      dateTime
        .set({
          hour: hour,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .toJSDate()
    )
  );
}

function getVisibleAreaStartAndEnd(
  timelineSettings: TimelineSettings,
  planningBoardSettings: PlanningBoardSettings
) {
  const {
    startDate,
    visibleArea,
    showWeekends,
    onlyShowWorkingHours,
  } = timelineSettings;

  const {
    visibleAreaOfDayStart,
    visibleAreaOfDayEnd,
    visibleAreaOfDayBufferInMinutes,
  } = planningBoardSettings;

  const startDateTime = DateTime.fromJSDate(startDate);

  let start: DateTime;
  let end: DateTime;

  const highlightDates: Date[] = [];
  const highlightHours: Date[] = [];

  if (visibleArea === TimelineVisibleArea.ONE_DAY) {
    start = onlyShowWorkingHours
      ? startDateTime.startOf("day").set({
          hour: visibleAreaOfDayStart.hour,
          minute: visibleAreaOfDayStart.minute,
          second: 0,
          millisecond: 0,
        })
      : startDateTime.startOf("day");
    end = start.set({
      hour: visibleAreaOfDayEnd.hour,
      minute: visibleAreaOfDayEnd.minute,
      second: 0,
      millisecond: 0,
    });
  } else if (visibleArea === TimelineVisibleArea.ONE_WEEK) {
    start = onlyShowWorkingHours
      ? startDateTime.startOf("day").set({
          hour: visibleAreaOfDayStart.hour,
          minute: visibleAreaOfDayStart.minute,
          second: 0,
          millisecond: 0,
        })
      : startDateTime.startOf("day");

    const weekday = start.weekday;
    const days = showWeekends ? 6 : weekday == 7 ? 5 : 6;

    end = onlyShowWorkingHours
      ? start.plus(Duration.fromObject({ days: days })).set({
          hour: visibleAreaOfDayEnd.hour,
          minute: visibleAreaOfDayEnd.minute,
          second: 0,
          millisecond: 0,
        })
      : start.plus(Duration.fromObject({ days: days })).endOf("day");

    const hoursToHighlight = [6, 9, 12, 15, 18];

    Interval.fromDateTimes(start, end)
      .splitBy({ days: 1 })
      .forEach((interval) => {
        const day = interval.start;

        if (day.weekday == 1 && start.weekday !== 1) {
          //Highlight start of week
          highlightDates.push(day.startOf("day").toJSDate());
        }

        addHighlightHoursForDay(highlightHours, day, hoursToHighlight);
      });
  } else {
    start = onlyShowWorkingHours
      ? startDateTime.startOf("week").set({
          hour: visibleAreaOfDayStart.hour,
          minute: visibleAreaOfDayStart.minute,
          second: 0,
          millisecond: 0,
        })
      : startDateTime.startOf("week");

    let numWeeks = 1;

    if (visibleArea === TimelineVisibleArea.TWO_WEEKS) {
      numWeeks = 2;
    } else if (visibleArea === TimelineVisibleArea.FOUR_WEEKS) {
      numWeeks = 4;
    }

    let daysOffset = showWeekends ? 7 : 5;

    for (let i = 0; i < numWeeks - 1; i++) {
      highlightDates.push(
        start
          .startOf("day")
          .plus(Duration.fromObject({ days: daysOffset }))
          .toJSDate()
      );
      daysOffset += 7;
    }

    end = start.plus(Duration.fromObject({ days: daysOffset })).set({
      hour: visibleAreaOfDayEnd.hour,
      minute: visibleAreaOfDayEnd.minute,
      second: 0,
      millisecond: 0,
    });

    if (visibleArea === TimelineVisibleArea.TWO_WEEKS) {
      const hoursToHighlight = [12];

      Interval.fromDateTimes(start, end)
        .splitBy({ days: 1 })
        .forEach((day) => {
          addHighlightHoursForDay(highlightHours, day.start, hoursToHighlight);
        });
    }
  }

  if (onlyShowWorkingHours) {
    return {
      start: start.plus({ minutes: -visibleAreaOfDayBufferInMinutes }),
      end: end.plus({ minutes: visibleAreaOfDayBufferInMinutes }),
      highlightDates,
      highlightHours,
    };
  } else {
    return {
      start: start,
      end: end.endOf("day"),
      highlightDates,
      highlightHours,
    };
  }
}

export function createTimelineConfigurationFromSettings(
  timelineSettings: TimelineSettings,
  planningBoardSettings: PlanningBoardSettings
): TimelineConfiguration {
  const {
    visibleAreaOfDayStart,
    visibleAreaOfDayEnd,
    visibleAreaOfDayBufferInMinutes,
  } = planningBoardSettings;

  const {
    start,
    end,
    highlightDates,
    highlightHours,
  } = getVisibleAreaStartAndEnd(timelineSettings, planningBoardSettings);

  const workDayStartDateTime = start
    .plus({ day: 1 })
    .set({
      hour: visibleAreaOfDayStart.hour,
      minute: visibleAreaOfDayStart.minute,
      second: 0,
      millisecond: 0,
    })
    .plus({ minutes: -visibleAreaOfDayBufferInMinutes });

  const workDayEndDateTime = start
    .set({
      hour: visibleAreaOfDayEnd.hour,
      minute: visibleAreaOfDayEnd.minute,
      second: 0,
      millisecond: 0,
    })
    .plus({ minutes: visibleAreaOfDayBufferInMinutes });

  const { showWeekends, onlyShowWorkingHours } = timelineSettings;

  const hiddenDates: HiddenDateData[] = [];

  const workDayStart = workDayStartDateTime.toJSDate();
  const workDayEnd = workDayEndDateTime.toJSDate();

  if (!showWeekends) {
    hiddenDates.push({
      start: "2022-07-09 00:00:00",
      end: "2022-07-11 00:00:00",
      repeatWeekly: true,
    });
  }

  if (onlyShowWorkingHours) {
    hiddenDates.push({
      start: workDayEnd.toISOString(),
      end: workDayStart.toISOString(),
      repeatDaily: true,
    });
  }

  return {
    start: start.toJSDate(),
    end: end.toJSDate(),
    highlightDates,
    highlightHours,
    visibleAreaOfDayStart: workDayStartDateTime.toJSDate(),
    visibleAreaOfDayEnd: workDayEndDateTime.toJSDate(),
    timelineSettings,
    planningBoardSettings,
    hiddenDates,
  };
}

export function createTimelineSettingsIncludingDate(
  date: Date,
  timelineSettings: TimelineSettings,
  timelineConfiguration: TimelineConfiguration
): TimelineSettings {
  const day = date.getDay();
  const isWeekend = day === 0 || day === 6;
  const showWeekends = isWeekend || timelineSettings.showWeekends;

  const { start, end } = timelineConfiguration;
  const dateTimestamp = date.getTime();

  if (start.getTime() <= dateTimestamp && dateTimestamp <= end.getTime()) {
    return {
      ...timelineSettings,
      showWeekends,
    };
  }

  return {
    ...timelineSettings,
    startDate: date,
    showWeekends,
  };
}
